import type { AlignmentStatus } from '../types/metro';
import { STATUS_THRESHOLDS } from './constants';

/**
 * Classify a credential-to-job ratio into an alignment status.
 *
 * DEC-056 (FINAL) binary cut at ratio = 1.0:
 *   ratio  < 1.0 → 'shortage'  (red/orange — under-producing credentials)
 *   ratio >= 1.0 → 'surplus'   (green — at-or-above parity)
 *
 * The legacy 0.8 / 1.2 three-band split (with a 'balanced' midband)
 * was retired — the source report doesn't argue for a balanced
 * classification, and Luis's continuous gradient (PR #37/#38)
 * already communicates "near parity" via the gold inflection at 1.0
 * without needing a discrete bucket. Edge case `ratio === 1.0`
 * counts as surplus by convention (matches CompareView's
 * `ratio < 1` check from PR #42 and DEC-056's "anything over 1 =
 * green" reading).
 */
export function getStatus(ratio: number): AlignmentStatus {
  if (!Number.isFinite(ratio)) {
    throw new Error(`getStatus: ratio must be a finite number, got ${ratio}`);
  }
  return ratio < STATUS_THRESHOLDS.surplusFrom ? 'shortage' : 'surplus';
}

/**
 * Format a credential-to-job ratio for display, e.g. 0.83 → "0.83".
 * Always shows two decimals so values align in tables.
 */
export function formatRatio(ratio: number): string {
  if (!Number.isFinite(ratio)) return '—';
  return ratio.toFixed(2);
}

/**
 * Format a count for display with locale-aware separators, e.g. 18000 → "18,000".
 * Negative numbers are formatted with a leading minus sign; non-finite returns "—".
 */
export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

/**
 * CR-017: render the "Annual credential shortage" display cell.
 *
 * Emma flagged that a shortage which ROUNDS DOWN to zero was rendered as an
 * em-dash, hiding a real (if tiny) shortage behind a "nothing here" glyph.
 * The rule she asked for: show the (truncated, possibly-zero) shortage
 * whenever the group is in shortage, and reserve the em-dash for groups with
 * NO shortage (ratio >= 1 / surplus). The discriminator is therefore the
 * DEC-056 `status`, NOT the shortage value — keyed off the shared status so
 * the 1.0 boundary lives in exactly one place (`getStatus`).
 *
 * `shortage` is `null` only in the legacy seed-math fallback (no real data),
 * which also renders an em-dash.
 */
export function shortageCell(status: AlignmentStatus, shortage: number | null): string {
  if (status === 'surplus' || shortage == null) return '—';
  return formatNumber(shortage);
}

/**
 * Combine class name fragments, dropping any that are falsy.
 * Lightweight alternative to `clsx` for small components.
 */
export function cn(...parts: ReadonlyArray<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Metro-wide annual credential shortage: jobs available minus
 * credentials produced, floored at 0 so a surplus metro reads as "no
 * shortage" rather than a negative number.
 *
 * This is the SAME formula already used for the "Annual credential
 * shortage" CSV column on Rankings (`src/pages/RankingsTable.tsx`
 * `LENS_CSV_COLUMNS`: `Math.max(0, j - c)`) and the per-group shortage
 * cell in the Map popup (`MetroPopup.tsx`: `Math.max(0, jobs - jobs *
 * ratio)`, which reduces to jobs − credentials). Extracted here as the
 * single source of truth for the METRO-LEVEL (all-groups) shortage so
 * the Map sidebar's "Annual credential shortage" sort key (CR-05,
 * 2026-07-08) reuses the established pattern instead of inventing a
 * new one.
 */
export function metroShortage(
  metro: Readonly<{ totalJobs: number; totalCredentials: number }>,
): number {
  return Math.max(0, metro.totalJobs - metro.totalCredentials);
}
